import type { SceneFileMap } from "./types";

export const normalizeAssetPath = (value: string | undefined | null): string => {
  if (!value) return "";
  const raw = String(value).trim().replace(/\\/g, "/");
  if (!raw) return "";
  const noQuery = raw.split("?")[0]?.split("#")[0] ?? raw;
  const parts = noQuery
    .split("/")
    .filter(Boolean)
    .filter((part) => part !== ".");
  return parts.join("/").toLowerCase();
};

export const basenameOfPath = (value: string): string => {
  const normalized = normalizeAssetPath(value);
  const parts = normalized.split("/");
  return parts[parts.length - 1] || normalized;
};

export function createSceneFileResolver(fileMap: SceneFileMap) {
  const exact = new Map<string, string[]>();
  const byBase = new Map<string, string[]>();
  const fileRefIds = new WeakMap<File, string>();
  let nextFileRefId = 1;

  const addIndex = (map: Map<string, string[]>, key: string, value: string) => {
    if (!key) return;
    const arr = map.get(key) || [];
    arr.push(value);
    map.set(key, arr);
  };

  Object.keys(fileMap).forEach((originalKey) => {
    const normalized = normalizeAssetPath(originalKey);
    if (!normalized) return;
    addIndex(exact, normalized, originalKey);
    addIndex(byBase, basenameOfPath(normalized), originalKey);
  });

  const dedupe = (values: string[]) => Array.from(new Set(values));
  const normalizeLookupPath = (path: string | undefined) =>
    typeof path === "string" ? path.trim().replace(/\\/g, "/") : "";
  const getFileIdentity = (key: string) => {
    const file = fileMap[key];
    if (!(file instanceof File)) return `missing:${key}`;
    let refId = fileRefIds.get(file);
    if (!refId) {
      refId = `ref:${nextFileRefId++}`;
      fileRefIds.set(file, refId);
    }
    return `${refId}:${file.name}:${file.size}:${file.lastModified}`;
  };
  const collapseEquivalentCandidates = (candidates: string[]) => {
    const byIdentity = new Map<string, string>();
    candidates.forEach((candidate) => {
      const identity = getFileIdentity(candidate);
      if (!byIdentity.has(identity)) byIdentity.set(identity, candidate);
    });
    return Array.from(byIdentity.values());
  };

  const candidateSetForPath = (path: string | undefined) => {
    const normalizedPath = normalizeAssetPath(path);
    if (!normalizedPath) {
      return {
        requestedPath: path || "",
        normalizedPath,
        candidates: [] as string[],
      };
    }

    const candidates: string[] = [];

    // 1. Exact normalized match.
    candidates.push(...(exact.get(normalizedPath) || []));

    // 2. Only if exact failed: try stripping or adding the "assets/" prefix.
    if (candidates.length === 0) {
      if (normalizedPath.startsWith("assets/")) {
        candidates.push(...(exact.get(normalizedPath.replace(/^assets\//, "")) || []));
      } else {
        candidates.push(...(exact.get(`assets/${normalizedPath}`) || []));
      }
    }

    // 3. Only if still nothing: try package-relative suffixes before basename fallback.
    // ROS URDFs commonly reference package://pkg_name/meshes/visual/link.stl,
    // while browser folder uploads may preserve only packageRoot/meshes/... or meshes/....
    // Matching these suffixes keeps visual/link.stl distinct from collision/link.stl.
    if (candidates.length === 0) {
      const parts = normalizedPath.split("/").filter(Boolean);
      const suffixes = parts
        .slice(1, -1)
        .map((_, index) => parts.slice(index + 1).join("/"))
        .filter((suffix) => suffix && suffix.includes("/"));
      suffixes.forEach((suffix) => {
        candidates.push(...(exact.get(suffix) || []));
        exact.forEach((values, key) => {
          if (key.endsWith(`/${suffix}`)) candidates.push(...values);
        });
      });
    }

    // 4. Only if still nothing: basename fallback.
    if (candidates.length === 0) {
      candidates.push(...(byBase.get(basenameOfPath(normalizedPath)) || []));
    }

    return {
      requestedPath: path || "",
      normalizedPath,
      candidates: dedupe(candidates),
    };
  };

  return {
    hasExactFile(path: string | undefined) {
      const normalizedLookupPath = normalizeLookupPath(path);
      if (!normalizedLookupPath) return false;
      const directKeys = dedupe([normalizedLookupPath, normalizedLookupPath.replace(/^\/+/, "")]);
      if (directKeys.some((candidate) => Boolean(fileMap[candidate]))) return true;
      const details = candidateSetForPath(path);
      const collapsedCandidates = collapseEquivalentCandidates(details.candidates);
      return collapsedCandidates.length === 1;
    },

    resolve(path: string | undefined) {
      const normalizedLookupPath = normalizeLookupPath(path);
      if (normalizedLookupPath) {
        const directKeys = dedupe([normalizedLookupPath, normalizedLookupPath.replace(/^\/+/, "")]);
        const directKey = directKeys.find((candidate) => Boolean(fileMap[candidate]));
        if (directKey) {
          return {
            requestedPath: path || "",
            normalizedPath: normalizeAssetPath(path),
            candidates: [directKey],
            file: fileMap[directKey],
            resolvedKey: directKey,
            isAmbiguous: false,
          };
        }
      }

      const details = candidateSetForPath(path);
      const collapsedCandidates = collapseEquivalentCandidates(details.candidates);
      const count = collapsedCandidates.length;
      return {
        ...details,
        candidates: collapsedCandidates,
        file: count === 1 ? fileMap[collapsedCandidates[0]] : null,
        resolvedKey: count === 1 ? collapsedCandidates[0] : null,
        isAmbiguous: count > 1,
      };
    },
  };
}
